import Alpine from "alpinejs"

const states = {
    result: "result",
    searching: "searching",
    empty: "empty",
}

Alpine.data("materialFinder2", () => ({
    filters: [],

    init() {
        this.$el.addEventListener("dropdown:register", (event) => {
            const { filterId, items } = event.detail
            this.filters[filterId] = { items, target: event.target }
        })
    },
    async query() {

    }
}))

Alpine.data("materialFinder", () => ({
    states: states,
    // available states
    state: "empty",

    // user input
    searchTerm: "",

    // async search result
    result: {},
    activeType: "reset",

    filters: {}, // Stores selected items by filter ID
    filtersList: [], // Stores references to each dropdown
    activeFilter: null, // Tracks currently active filter ID
    activeFilterTags: [], // Tracks currently active filter tags

    searchFilterList: [], // Stores references to each search filter

    searchDebounceTimer: null,
    visibilityLimit: 10,
    perPage: 10,
    pages: 1,
    currentPage: 1,
    currentSearchAbortController: null,

    init() {
        // Initialize with result state
        this.setState("result")

        // Always initialize with "all" as the active type
        this.activeType = "all"

        // Execute dummy query on load if in preview mode
        if (document.body.classList.contains("preview-body") && this.$root.dataset.dummyresult) {
            this.dummyQuery(true) // Pass true to indicate initial load
        } else {
            this.query()
        }

        this.$el.addEventListener("searchFilter:add-item", (event) => {
            if (event.detail && event.detail.identifier) {
                this.activeType = event.detail.identifier
                if (this.result && this.result[this.activeType]) {
                    this.activeTypeResult = this.result[this.activeType]
                }
            }
        })

        this.$el.addEventListener("searchFilter:clear-items", () => {
            // Always ensure activeType is set to "all" when filters are cleared
            this.activeType = "all"
            if (this.result && this.result[this.activeType]) {
                this.activeTypeResult = this.result[this.activeType]
            }
        })

        // Get references to all filter dropdowns (excluding sort)
        this.filtersList = Array.from(
            this.$el.querySelectorAll(
                '.c-dropdown[data-filter-id="vaccinations"], .c-dropdown[data-filter-id="targetgroups"]',
            ),
        )

        // Get references to all search filters - both mobile and desktop versions
        this.searchFilterList = Array.from(this.$el.querySelectorAll('.c-searchFilter[data-filter-id="types"]'))

        // Ensure "All/Reset" is selected by default in search filters
        // Use setTimeout to ensure that Splide initialization completes first
        setTimeout(() => {
            this.searchFilterList.forEach((filter) => {
                filter.dispatchEvent(
                    new CustomEvent("searchFilter:reset", {
                        bubbles: true, // Changed to true to ensure proper propagation
                    }),
                )
                // Explicitly set Alpine store value to ensure consistency
                Alpine.store("filterState").activeType = "reset"
            })
        }, 50) // Small delay to ensure proper initialization sequence

        // Listen for `dropdown:add-item` to handle item selection from child
        this.$el.addEventListener("dropdown:add-item", (event) => this.handleAddItem(event))
        this.$el.addEventListener("dropdown:remove-item", (event) => this.handleRemoveItem(event))
        this.$el.addEventListener("dropdown:clear-items", (event) => this.handleClearItems(event))

        this.$watch("searchTerm", () => {
            if (this.searchTerm.length > 0) {
                // Set state to searching
                this.state = states.searching

                const debounceSearch = (func, delay) => {
                    return () => {
                        const context = this
                        const args = arguments
                        clearTimeout(this.searchDebounceTimer)
                        this.searchDebounceTimer = setTimeout(() => func.apply(context, args), delay)
                    }
                }

                debounceSearch(() => {
                    this.search()
                }, 500)()
            } else {
                // When search is cleared, reset to result state and reset filter to "All"
                this.setState("result")
                // Reset the filter to "All" when search is cleared
                this.searchFilterList.forEach((filter) => {
                    filter.dispatchEvent(
                        new CustomEvent("searchFilter:reset", {
                            bubbles: true, // Changed to true for consistency
                        }),
                    )
                    // Also directly update Alpine store
                    Alpine.store("filterState").activeType = "reset"
                })
                // Reset the type to "all"
                this.activeType = "all"
                // Refresh the results
                this.query()
            }
        })

        this.$watch("activeType", () => {
            this.currentPage = 1
            this.search()
        })

        this.$watch("currentPage", (value) => {
            this.query(value > 1)
        })

        // Initialize from URL parameters if they exist
        this.initFromUrlParams()
    },

    // Initialize filters and sort from URL parameters
    initFromUrlParams() {
        const url = new URL(window.location.href)

        // Handle search term parameter (triggers the searchTerm $watch, which runs the search)
        const searchTermValue = url.searchParams.get("q")
        if (searchTermValue) {
            this.searchTerm = searchTermValue
        }

        // Handle sort parameter
        const sortValue = url.searchParams.get("sort")
        if (sortValue) {
            this.handleAddItem({
                detail: {
                    id: "sort",
                    identifier: sortValue,
                    title: sortValue, // You might want to map this to a proper title
                },
            })
        }

        // Parameter mapping (URL parameter name to internal filter ID)
        const paramToFilterMap = {
            vaccination: "vaccinations",
            targetgroup: "targetgroups",
        }

        // Handle filter parameters
        Object.entries(paramToFilterMap).forEach(([paramName, filterId]) => {
            const filterValue = url.searchParams.get(paramName)
            if (filterValue) {
                filterValue.split(",").forEach((value) => {
                    this.handleAddItem({
                        detail: {
                            id: filterId,
                            identifier: value,
                            title: value, // You might want to map this to a proper title
                        },
                    })
                })
            }
        })
    },

    // Dummy query for preview mode
    dummyQuery(isInitialLoad = false) {
        this.setState("searching")
        setTimeout(() => {
            if (isInitialLoad || this.searchTerm.length > 5) {
                let result = []
                try {
                    result = JSON.parse(this.$root.dataset.dummyresult)
                } catch (e) {
                    // ignore...
                }

                this.result = result
                this.setState("result")
                if (this.result[this.activeType]) {
                    this.activeTypeResult = this.result[this.activeType]
                }
            } else {
                this.result = {}
                this.setState("empty")
            }
        }, 500)
    },

    setState(newState) {
        if (!Object.values(states).includes(newState)) {
            return false
        }

        // Simply set the state to the new value
        this.state = newState
        return true
    },

    search() {
        if (document.body.classList.contains("preview-body") && this.$root.dataset.dummyresult) {
            this.dummyQuery()
        } else {
            this.query()
        }
    },

    clearSearch() {
        this.searchTerm = ""
    },

    getEntryPointIdentifier() {
        const filters = Object.values(this.filters)
        const activeFilter = filters.find((filter) => filter.length > 0 && filter.find(() => true).identifier) ?? []
        return activeFilter.length > 0 && activeFilter.find(() => true).identifier
    },
    // Modified query function to properly handle AbortController
    async query(append = false) {
        try {
            // Only abort previous request if not appending
            if (!append && this.currentSearchAbortController) {
                this.currentSearchAbortController.abort();
                this.currentSearchAbortController = null;
            }

            // Create new AbortController for this request
            this.currentSearchAbortController = new AbortController();

            // A selected vaccination/targetgroup filter scopes the search to that
            // specific node's subtree. Absent that, fall back to the current
            // audience's Home (population vs. Fachpersonen), so unfiltered search
            // doesn't leak content across audiences.
            const filterEntryPoint = this.getEntryPointIdentifier();
            const entryPoint = filterEntryPoint || this.$root.dataset.audienceEntryPoint;
            const excludeEntryPoint = !filterEntryPoint && this.$root.dataset.audienceExcludeEntryPoint;
            this.state = states.searching;

            const params = new URLSearchParams({
                q: this.searchTerm,
                type: this.activeType,
                page: this.currentPage,
                perPage: this.perPage,
                ...(entryPoint && { entryPoint }),
                ...(excludeEntryPoint && { excludeEntryPoint }),
            });

            const searchResult = await fetch(`${this.$root.dataset.uri}?${params}`, {
                signal: this.currentSearchAbortController.signal,
            }).then(data => data.json());

            if (append && this.result && this.result.items) {
                this.result.items.push(...searchResult.items);
            } else {
                this.result = searchResult;
            }
            this.pages = searchResult.pages;

            if (this.result.total > 0) {
                this.state = states.result;
            } else {
                this.state = states.empty;
            }
        } catch (error) {
            // Ignore AbortError as it's expected when we abort requests
            if (error.name !== 'AbortError') {
                console.error('Error during search query:', error);
                this.state = states.empty;
            }
        }
    },

    // Modified handleAddItem function to update URL parameters correctly and trigger query
    handleAddItem(event) {
        const { id, identifier, title } = event.detail

        // Map internal filter IDs to URL parameter names if needed
        const paramNameMap = {
            targetgroups: "targetgroup",
            vaccinations: "vaccination",
            sort: "sort",
        }

        // Get the correct parameter name for the URL
        const paramName = paramNameMap[id] || id

        if (id === "sort") {
            // Handle sort dropdown selection - single value
            this.updateUrlParam(paramName, identifier)
            // Trigger a new query with the updated sort parameter
            this.query()
            return
        }

        // Handle filter selection - can be multiple values
        if (!this.filters[id]) this.filters[id] = []
        if (!this.filters[id].some((item) => item.identifier === identifier)) {
            this.filters[id].push({ identifier, title })
            this.activeFilter = id
            this.activeFilterTags = this.filters[id].map((item) => item.identifier)
            this.updateDropdownStates(this.activeFilter)

            // Update URL with filter selection
            this.updateUrlParam(paramName, this.activeFilterTags.join(","))

            // Trigger a new query with the updated parameters
            this.query()
        }
    },

    // Modified handleClearItems function to use correct parameter names and trigger query
    handleClearItems(event) {
        const { id } = event.detail

        // Map internal filter IDs to URL parameter names if needed
        const paramNameMap = {
            targetgroups: "targetgroup",
            vaccinations: "vaccination",
            sort: "sort",
        }

        // Get the correct parameter name for the URL
        const paramName = paramNameMap[id] || id

        if (id === "sort") {
            // Clear sort parameter from URL
            this.updateUrlParam(paramName, null)
            // Trigger a new query with the updated parameters
            this.query()
            return
        }

        if (this.filters[id]) this.filters[id] = []
        if (this.activeFilter === id) {
            this.activeFilter = null
            this.activeFilterTags = []
        }

        // Clear the specific filter parameter from URL
        this.updateUrlParam(paramName, null)
        this.updateDropdownStates(null)

        // Check if all filters are now empty
        const allFiltersEmpty = Object.values(this.filters).every((filterArray) => filterArray.length === 0)

        if (allFiltersEmpty) {
            // Reset to initial view state if all filters are cleared
            this.setState("result")
            this.activeType = "all"
            // Reset pagination related properties
            this.page = 1
        }

        // Trigger a new query with the updated parameters
        this.query()
    },

    // Handle item removal from multiselect dropdown
    handleRemoveItem(event) {
        const { id, identifier } = event.detail

        const paramNameMap = {
            targetgroups: "targetgroup",
            vaccinations: "vaccination",
            sort: "sort",
        }

        // Get the correct parameter name for the URL
        const paramName = paramNameMap[id] || id

        // Remove the item from the filter array
        if (this.filters[id]) {
            this.filters[id] = this.filters[id].filter(item => item.identifier !== identifier)

            if (this.filters[id].length === 0) {
                if (this.activeFilter === id) {
                    this.activeFilter = null
                    this.activeFilterTags = []
                }
                this.updateUrlParam(paramName, null)
            } else {
                this.activeFilterTags = this.filters[id].map((item) => item.identifier)
                this.updateUrlParam(paramName, this.activeFilterTags.join(","))
            }
            this.updateDropdownStates(Object.values(this.filters).some(f => f.length > 0) ? id : null)
            this.query()
        }
    },

    updateUrlParam(param, value) {
        const url = new URL(window.location.href)
        if (value === null) {
            url.searchParams.delete(param)
        } else {
            url.searchParams.set(param, value)
        }
        window.history.replaceState({}, "", url)
    },

    updateDropdownStates(enabledId) {
        this.filtersList.forEach((filter) => {
            filter.dispatchEvent(
                new CustomEvent("dropdown:disable-filter", {
                    bubbles: true,
                    detail: { enabledId },
                }),
            )
        })
    },

    // Modified resetAllFilters function to ensure "All" filter is always selected
    resetAllFilters() {
        // Reset all filter-related state
        this.filters = {}
        this.activeFilter = null
        this.activeFilterTags = []
        this.clearSearch()
        this.showAllItems = false
        this.visibilityLimit = 10
        this.currentPage = 1

        // Clear all filter and sort parameters from URL
        this.updateUrlParam("targetgroup", null)
        this.updateUrlParam("vaccination", null)
        this.updateUrlParam("sort", null)
        this.updateDropdownStates(null)

        // Reset to initial view state
        this.setState("result")

        // Explicitly set the active type to "all"
        this.activeType = "all"

        // Reset all search filters with a small delay to ensure proper Splide initialization
        setTimeout(() => {
            this.searchFilterList.forEach((filter) => {
                // First reset the filter component's internal state
                filter.dispatchEvent(new CustomEvent("searchFilter:reset", { bubbles: true }))

                // Then explicitly set Alpine store value
                Alpine.store("filterState").activeType = "reset"
            })

            // Reset all dropdowns
            this.filtersList.forEach((filter) => {
                filter.dispatchEvent(
                    new CustomEvent("dropdown:clear-items", {
                        bubbles: true,
                        detail: { id: filter.getAttribute("data-filter-id") },
                    }),
                )
            })

            // Trigger a new query with the reset parameters to show initial state
            this.query()
        }, 50)
    },

    matchesCriteria() {
        return true
    },

    showMore() {
        this.currentPage++
    },

    showAll() {
        this.showAllItems = true
        this.visibilityLimit = Infinity
    },

    getVisibleCount() {
        if (!this.result || !this.result.items) return 0
        return Math.min(this.currentPage * this.perPage, this.result.total)
    },

    getTotalCount() {
        return this.result.total
    },
}))
